// chatContext/composerDraftHandoff.ts
//
// Unsent composer text carried across a surface swap (ORA-736). Opening a
// document from the study drawer swaps the drawer for the floating shell's
// document view, and closing it swaps them back; each swap unmounts one
// composer and mounts another on the same conversation. The composer that
// leaves stashes its text, and the next composer on that conversation takes
// it. Nothing is kept outside a swap: an ordinary unmount stashes nothing.

let isHandoffPending = false;
const drafts = new Map<string, string>();
/**
 * The study drawer's thread position, so the drawer that comes back after the
 * document view opens where the student was rather than at the bottom. It
 * outlives the swap that stored it (the composer ends each swap on its first
 * mount), and the drawer's return takes it; a later swap overwrites it.
 */
const threadScrolls = new Map<string, number>();

/** Called just before a surface swap that remounts the composer. */
export const beginComposerDraftHandoff = (): void => {
  isHandoffPending = true;
  drafts.clear();
};

/** A leaving thread's scroll offset, stored only during a swap. */
export const stashThreadScroll = (key: string, scrollTop: number): void => {
  if (!isHandoffPending) return;
  threadScrolls.set(key, scrollTop);
};

/** Where a thread mounting on `key` in a swap should open, or null. */
export const takeThreadScroll = (key: string): number | null => {
  if (!isHandoffPending) return null;

  const scrollTop = threadScrolls.get(key) ?? null;
  threadScrolls.delete(key);
  return scrollTop;
};

/** The leaving composer's text, kept only while a swap is pending. */
export const stashComposerDraft = (key: string, text: string): void => {
  if (!isHandoffPending || !text.trim()) return;
  drafts.set(key, text);
};

/**
 * The text a composer mounting on `key` should start from, or null. The swap
 * ends with the first composer that mounts, whether or not a draft was left.
 */
export const takeComposerDraft = (key: string): string | null => {
  if (!isHandoffPending) return null;

  const text = drafts.get(key) ?? null;
  isHandoffPending = false;
  drafts.clear();
  return text;
};

/** Test-only reset. */
export const resetComposerDraftHandoff = (): void => {
  isHandoffPending = false;
  drafts.clear();
  threadScrolls.clear();
};
